<template>
    <div :class="['input_group', { 'image_error': errorMessage }]">
        <button v-show="!imageSrc && !preview" type="button" @click="triggerFileInput" class="image_upload">
            <h4>Selecionar imagem</h4>
            <span>Tamanho mínimo: {{ minWidth }}x{{ minHeight }}</span>
        </button>
        <input ref="fileInput" type="file" @change="handleFileChange" class="file_input" accept="image/*" />
        <img v-if="previewImage" :src="previewImage" @click="triggerFileInput" class="image_preview" />
        <img v-else-if="imageSrc" :src="imageSrc" @click="triggerFileInput" class="image_preview" />
        <span v-if="errorMessage" class="error_message">{{ errorMessage }}</span>
    </div>
</template>
  
<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'

const props = defineProps({
    error: String,
    preview: String,
    minWidth: {
        type: Number,
        default: 1200,
    },
    minHeight: {
        type: Number,
        default: 800,
    },
    /*
     * Mesmo teto do Upload::putImage na API. A capa vira 1200x800, então acima
     * disso só sobra custo de processamento no servidor.
     */
    maxPixels: {
        type: Number,
        default: 25000000,
    },
})

const emits = defineEmits(['update:modelValue']);
const imageSrc = ref('');
const previewImage = ref(props.preview);
const fileInput = ref(null);

function readDimensions(url) {
    return new Promise((resolve, reject) => {
        const image = new window.Image();

        image.onload = () => {
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
        };

        image.onerror = () => {
            reject(new Error('Arquivo não pôde ser lido como imagem'));
        };

        image.src = url;
    });
}

function rejectFile(message, url) {
    URL.revokeObjectURL(url);

    errorMessage.value = message;
    imageSrc.value = '';
    emits('update:modelValue', null);

    if (fileInput.value) {
        fileInput.value.value = '';
    }
}

async function handleFileChange(event) {
    const file = event.target.files[0];

    if (!file) {
        return;
    }

    const url = URL.createObjectURL(file);
    let dimensions = null;

    try {
        dimensions = await readDimensions(url);
    } catch (e) {
        rejectFile('Arquivo não é uma imagem válida, escolha outro', url);
        return;
    }

    if (dimensions.width < props.minWidth || dimensions.height < props.minHeight) {
        rejectFile(`Imagem menor que ${props.minWidth}x${props.minHeight}, escolha outra`, url);
        return;
    }

    if (dimensions.width * dimensions.height > props.maxPixels) {
        const megapixels = Math.round(props.maxPixels / 1000000);
        rejectFile(`Imagem grande demais (máximo ${megapixels} MP), reduza antes de enviar`, url);
        return;
    }

    if (imageSrc.value) {
        URL.revokeObjectURL(imageSrc.value);
    }

    imageSrc.value = url;
    emits('update:modelValue', file);
    previewImage.value = null;
    handleError();
}

function triggerFileInput() {
    fileInput.value.click();
}

const errorMessage = ref(props.error);

watch(() => props.error, (value) => {
  errorMessage.value = value || null;
});

watch(() => props.preview, (value) => {
  if(value){
    previewImage.value = value;
  }
});

const handleError = () => {
  errorMessage.value = null;
};
</script>
  
<style scoped>
.image_upload {
    height: 150px;
    background-color: #ebebeb;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    cursor: pointer;
    border: 0;
    width: 100%;
}

.dark .image_upload {
    background-color: #232932;
    border: 1px#394251 solid;
}

.image_upload:hover {
    background-color: #d7d7d7;
}

.dark .image_upload:hover {
    background-color: #1c2128;
}

.image_upload h4 {
    font-size: 16px;
    margin-bottom: 0;
}

.dark .image_upload h4 {
    color: #b5b9d0;
}

.image_upload span {
    font-size: 14px;
    font-weight: 500;
    color: #7f7f7f;
}

.dark .image_upload span {
    color: #9fa3ba;
}

.file_input {
    display: none;
}

.image_preview {
    max-width: 100%;
    height: auto;
    margin-top: 10px;
    border-radius: 4px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
}

.image_preview:hover {
    opacity: 0.7;
}

.image_error button {
    border: 1px #df2b2b solid;
    color: #df2b2b;
    box-shadow: 0px 0px 0px 3px rgb(255 136 136 / 30%);
}

.error_message {
    color: #df2b2b;
    font-weight: 500;
    margin-top: 4px;
    display: block;
}
</style>
  